"use client";
import { Header } from '@/pages/home/sections/header';
import { Footer } from '@/pages/home/sections/footer';
import { StandardCTA } from '@/components/standard-cta';
import { motion } from 'framer-motion';
import { Check, Clock, Users, Calendar, Star, Video, MessageSquare, Gem, Calculator, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { allCourses } from '@/pages/courses/data/courses';
import { useNavigate } from 'react-router-dom';

const packages = [
  {
    name: 'Standard',
    badge: 'Most Popular',
    type: '1-on-1 Class',
    priceRange: { start: 10, end: 8 },
    description: 'Perfect for consistent learning with a structured schedule. Great value for dedicated students.',
    features: [
      '1-on-1 personalized instruction',
      'Class by class feedback',
      'Fixed schedule',
      'Progress tracking'
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'VIP',
    badge: 'Premium',
    type: '1-on-1 Class',
    priceRange: { start: 12, end: 10 },
    description: 'Maximum flexibility with recorded classes. Perfect for busy schedules and those who want to review.',
    features: [
      '1-on-1 personalized instruction',
      'Class by class feedback',
      'Flexible schedule',
      'Recorded classes',
      'Progress tracking',
      'Priority booking'
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
];

// Pricing Calculator Component
function PricingCalculator() {
  const [classesPerWeek, setClassesPerWeek] = useState(2)
  const [minutes, setMinutes] = useState(30)
  const [packageType, setPackageType] = useState<'standard' | 'vip'>('standard')
  
  // Pricing tiers based on classes per week
  const pricingTiers = {
    standard: {
      1: 10,  // 1 class/week = $10/hr
      2: 9.5, // 2 classes/week = $9.5/hr
      3: 9,   // 3 classes/week = $9/hr
      4: 8.5, // 4 classes/week = $8.5/hr
      5: 8.25,// 5 classes/week = $8.25/hr
      6: 8    // 6 classes/week = $8/hr
    },
    vip: {
      1: 12,  // 1 class/week = $12/hr
      2: 11.5,// 2 classes/week = $11.5/hr
      3: 11,  // 3 classes/week = $11/hr
      4: 10.5,// 4 classes/week = $10.5/hr
      5: 10.25,// 5 classes/week = $10.25/hr
      6: 10   // 6 classes/week = $10/hr
    }
  }
  
  const rate = pricingTiers[packageType][classesPerWeek as keyof typeof pricingTiers.standard]
  const classesPerMonth = classesPerWeek * 4 // Approximate 4 weeks per month
  const hours = (classesPerMonth * minutes) / 60
  const total = hours * rate
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#245E51]/10 flex items-center justify-center">
          <Calculator className="h-6 w-6 text-[#245E51]" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Calculate Your Monthly Cost</h3>
          <p className="text-sm text-gray-600">Flexible monthly plans based on your needs</p>
        </div>
      </div>
      
      {/* Package Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Choose Package
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setPackageType('standard')}
            className={`p-4 rounded-xl border-2 transition-all ${
              packageType === 'standard'
                ? 'border-[#245E51] bg-[#245E51]/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="font-semibold text-gray-900 mb-1">Standard</div>
            <div className="text-xs text-gray-600">$10-8/hour</div>
          </button>
          <button
            onClick={() => setPackageType('vip')}
            className={`p-4 rounded-xl border-2 transition-all relative ${
              packageType === 'vip'
                ? 'border-[#245E51] bg-[#245E51]/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <span className="absolute -top-2 -right-2 px-2 py-0.5 text-[10px] font-bold bg-[#A8FF51] text-[#245E51] rounded-full">
              Popular
            </span>
            <div className="font-semibold text-gray-900 mb-1">VIP</div>
            <div className="text-xs text-gray-600">$12-10/hour</div>
          </button>
        </div>
      </div>
      
      {/* Classes per Week */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Classes per Week
        </label>
        <select
          value={classesPerWeek}
          onChange={(e) => setClassesPerWeek(Number(e.target.value))}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#245E51]/20 focus:border-[#245E51] outline-none"
        >
          <option value="1">1 class/week</option>
          <option value="2">2 classes/week</option>
          <option value="3">3 classes/week</option>
          <option value="4">4 classes/week</option>
          <option value="5">5 classes/week</option>
          <option value="6">6 classes/week</option>
        </select>
      </div>
      
      {/* Minutes per Class */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Class Duration
        </label>
        <select
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#245E51]/20 focus:border-[#245E51] outline-none"
        >
          <option value="30">30 minutes</option>
          <option value="45">45 minutes</option>
          <option value="60">60 minutes</option>
          <option value="90">90 minutes</option>
        </select>
      </div>
      
      {/* Calculation Summary */}
      <div className="bg-gradient-to-br from-[#245E51]/5 to-[#A8FF51]/10 rounded-2xl p-6 mb-6">
        <div className="space-y-2 text-sm text-gray-600 mb-4">
          <div className="flex justify-between">
            <span>Classes per Week:</span>
            <span className="font-semibold text-gray-900">{classesPerWeek} classes</span>
          </div>
          <div className="flex justify-between">
            <span>Classes per Month:</span>
            <span className="font-semibold text-gray-900">{classesPerMonth} classes</span>
          </div>
          <div className="flex justify-between">
            <span>Duration:</span>
            <span className="font-semibold text-gray-900">{minutes} minutes</span>
          </div>
          <div className="flex justify-between">
            <span>Total Hours/Month:</span>
            <span className="font-semibold text-gray-900">{hours.toFixed(1)} hours</span>
          </div>
          <div className="flex justify-between">
            <span>Rate:</span>
            <span className="font-semibold text-gray-900">${rate}/hour</span>
          </div>
        </div>
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-between items-baseline">
            <span className="text-gray-700 font-medium">Monthly Cost:</span>
            <div>
              <span className="text-4xl font-bold text-[#245E51]">
                ${Math.round(total)}
              </span>
              <span className="text-gray-600 text-sm">/month</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA */}
      <a href="/contact" className="block w-full py-4 bg-gradient-to-r from-[#245E51] to-[#1a4a3e] text-white rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] text-center">
        Request Custom Offer
      </a>
      
      {/* Note */}
      <p className="text-xs text-gray-500 text-center mt-4">
        * More classes per week = Lower hourly rate
      </p>
    </motion.div>
  )
}

export default function PricingPage(){
  const navigate = useNavigate()
  
  // Get premium courses
  const premiumCourses = allCourses.filter(c => c.isPremiumCourse).slice(0, 3)
  
  return (
    <div className="min-h-screen bg-[#FCF8F1]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 sm:pt-40 sm:pb-16 lg:pt-48 lg:pb-20">
          <div className="w-[90%] max-w-7xl mx-auto">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-3 py-1 text-sm font-medium text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30">
                Flexible Hour-Based Pricing
              </div>
              <motion.h1 
                initial={{ opacity:0, y:26 }} 
                animate={{ opacity:1, y:0 }} 
                transition={{ duration:.7, ease:[0.23,0.68,0.32,0.97] }} 
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-gray-900"
              >
                Pay Only for What You Need
              </motion.h1>
              <motion.p 
                initial={{ opacity:0, y:30 }} 
                animate={{ opacity:1, y:0 }} 
                transition={{ duration:.8, delay:.06, ease:[0.23,0.68,0.32,0.97] }} 
                className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed"
              >
                Choose your package and customize your schedule. Hour-based pricing means you only pay for actual learning time.
              </motion.p>
              <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#245E51]" /> Cancel anytime</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#245E51]" /> All ages welcome</div>
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#245E51]" /> Flexible scheduling</div>
                <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#245E51]" /> 4.9/5 rating</div>
              </div>
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="pb-16">
          <div className="w-[90%] max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {packages.map((pkg, i) => (
                <motion.div 
                  key={pkg.name} 
                  initial={{ opacity:0, y:28 }} 
                  whileInView={{ opacity:1, y:0 }} 
                  viewport={{ once:true, amount:.4 }} 
                  transition={{ duration:.6, delay:i*0.1, ease:[0.23,0.68,0.32,0.97] }}
                  className={`relative rounded-3xl border ${pkg.popular ? 'bg-white shadow-xl border-gray-100 ring-2 ring-[#A8FF51]/60' : 'bg-white/80 backdrop-blur-xl border-white/50 shadow-lg'} p-8`}
                >
                  {pkg.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full bg-[#245E51] text-white shadow">{pkg.badge}</span>
                  )}
                  {!pkg.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full bg-white text-gray-800 border border-gray-200 shadow">{pkg.badge}</span>
                  )}
                  <div className="mt-2 flex h-full flex-col">
                    <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{pkg.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{pkg.type}</p>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">{pkg.description}</p>
                    <div className="mt-6 flex items-baseline gap-2">
                      <span className="text-5xl font-extrabold tracking-tight text-gray-900">${pkg.priceRange.start}</span>
                      <span className="text-gray-600">/hour</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      Starting price • Decreases to ${pkg.priceRange.end}/hr with commitment
                    </div>
                    <ul className="mt-6 space-y-3 text-sm">
                      {pkg.features.map(f => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#245E51] mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-8">
                      <a href="#calculator" className={`inline-flex justify-center w-full px-4 py-3 rounded-xl font-semibold shadow transition ${pkg.popular ? 'bg-[#245E51] text-white hover:shadow-lg' : 'bg-white text-gray-900 border border-gray-200 hover:shadow-md'}`}>
                        {pkg.cta}
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Calculator */}
            <div id="calculator">
              <PricingCalculator />
            </div>
          </div>
        </section>

        {/* Premium Courses Section */}
        <section className="py-20 bg-gradient-to-br from-[#245E51] to-[#1a4a3e] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-5"></div>
          <div className="w-[90%] max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-white/20 rounded-full border border-white/30 mb-4">
                <Gem className="w-4 h-4 mr-1" />
                Special Pricing
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                Premium Courses
              </h2>
              <p className="text-xl text-white/80 max-w-2xl mx-auto">
                3 specialized courses with flat-rate pricing at <span className="font-bold text-[#A8FF51]">$12/hour</span>
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Quranic Arabic (Arabic to Understand Quran)',
                  description: 'Understand the Quran directly without translation',
                  features: ['Root patterns', 'Morphology', 'Direct comprehension']
                },
                {
                  title: 'Arabic for Speaking',
                  description: 'Speak Modern Standard Arabic fluently',
                  features: ['Conversation focus', 'Real situations', 'Intensive practice']
                },
                {
                  title: 'Egyptian Arabic Dialect',
                  description: 'The most widely understood Arabic dialect',
                  features: ['Daily conversation', 'Media understanding', 'Cultural context']
                }
              ].map((course, index) => (
                <motion.div
                  key={course.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="bg-white rounded-2xl p-8 shadow-xl"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#A8FF51]/20 flex items-center justify-center mb-4">
                    <Gem className="h-6 w-6 text-[#245E51]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{course.description}</p>
                  <div className="text-3xl font-bold text-[#245E51] mb-4">
                    $12<span className="text-lg text-gray-600">/hour</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {course.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className="h-4 w-4 text-[#245E51]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => navigate('/courses')}
                    className="w-full py-3 bg-[#245E51] text-white rounded-xl font-semibold hover:bg-[#1a4a3e] transition"
                  >
                    Learn More
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Table */}
        <section className="py-16 bg-white">
          <div className="w-[90%] max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                Transparent Pricing Structure
              </h2>
              <p className="text-gray-600">
                More classes per week means better rates. Choose what works for you.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Standard Pricing Table */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden"
              >
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900">Standard Package</h3>
                  <p className="text-sm text-gray-600 mt-1">Fixed schedule</p>
                </div>
                <div className="p-6">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 text-sm font-semibold text-gray-700">Classes/Week</th>
                        <th className="text-right py-3 text-sm font-semibold text-gray-700">Rate/Hour</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-3 text-gray-900">1 class/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$10/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">2 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$9.5/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">3 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$9/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">4 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$8.5/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">5 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$8.25/hr</td>
                      </tr>
                      <tr className="bg-[#A8FF51]/10">
                        <td className="py-3 text-gray-900 font-semibold">6 classes/week</td>
                        <td className="py-3 text-right font-bold text-[#245E51]">$8/hr</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* VIP Pricing Table */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl border-2 border-[#245E51] relative overflow-visible"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#245E51] text-white text-xs font-bold rounded-full">
                  Most Popular
                </div>
                <div className="bg-[#245E51]/5 px-6 py-4 border-b border-[#245E51]/20">
                  <h3 className="text-xl font-bold text-gray-900">VIP Package</h3>
                  <p className="text-sm text-gray-600 mt-1">Flexible + Recorded</p>
                </div>
                <div className="p-6">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 text-sm font-semibold text-gray-700">Classes/Week</th>
                        <th className="text-right py-3 text-sm font-semibold text-gray-700">Rate/Hour</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-3 text-gray-900">1 class/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$12/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">2 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$11.5/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">3 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$11/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">4 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$10.5/hr</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-gray-900">5 classes/week</td>
                        <td className="py-3 text-right font-semibold text-gray-900">$10.25/hr</td>
                      </tr>
                      <tr className="bg-[#A8FF51]/20">
                        <td className="py-3 text-gray-900 font-semibold">6 classes/week</td>
                        <td className="py-3 text-right font-bold text-[#245E51]">$10/hr</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>

            {/* Note */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center text-sm text-gray-600 mt-8"
            >
              💡 Example: 2 classes/week × 45 minutes × 4 weeks = 6 hours/month
            </motion.p>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-white/60 backdrop-blur-xl border-y border-white/40">
          <div className="w-[90%] max-w-6xl mx-auto">
            <div className="max-w-3xl mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Everything You Need</h2>
              <p className="mt-3 text-gray-600">All packages include professional instruction and comprehensive support</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Users, title: 'Certified Teachers', text: 'Egyptian certified Quran teachers with UK management' },
                { icon: Video, title: 'Live 1-on-1 Sessions', text: 'Personal attention in every class' },
                { icon: MessageSquare, title: 'Continuous Feedback', text: 'Class by class progress tracking' },
                { icon: Calendar, title: 'Flexible Scheduling', text: 'Choose times that work for you' },
                { icon: Clock, title: 'No Long Contracts', text: 'Cancel or pause anytime' },
                { icon: Star, title: 'High Quality', text: '4.9/5 average student rating' },
              ].map((item, i) => (
                <motion.div 
                  key={item.title} 
                  initial={{ opacity:0, y:22 }} 
                  whileInView={{ opacity:1, y:0 }} 
                  viewport={{ once:true, amount:.4 }} 
                  transition={{ duration:.55, delay:i*0.04 }} 
                  className="rounded-2xl bg-white border border-gray-100 shadow-md hover:shadow-lg transition-all p-6"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#245E51]/10 to-[#A8FF51]/20 text-[#245E51] mb-3">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-gray-900 tracking-tight">{item.title}</h3>
                  <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto w-[90%]">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Common Questions</h2>
              <p className="mt-4 text-gray-600 leading-relaxed text-base">Everything you need to know about our pricing</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { q: 'How does hour-based pricing work?', a: 'You pay based on the actual hours you take. Book 4, 8, 12, or more classes per month at your preferred duration (30, 45, 60, or 90 minutes).' },
                { q: 'What\'s the difference between Standard and VIP?', a: 'VIP includes recorded classes and flexible scheduling, while Standard has a fixed schedule. Both offer the same high-quality 1-on-1 instruction.' },
                { q: 'Why are some courses priced at $12/hour?', a: 'Our 3 premium courses (Quranic Arabic, Arabic for Speaking, Egyptian Dialect) have specialized content and fixed pricing at $12/hour.' },
                { q: 'Can I switch between packages?', a: 'Yes, you can upgrade or change packages at any time. Contact our team for smooth transitions.' },
                { q: 'Do prices really decrease?', a: 'Yes! With longer commitments, Standard goes from $10 to $8/hour, and VIP from $12 to $10/hour.' },
                { q: 'Is there a minimum commitment?', a: 'No minimum! Month-to-month billing. Cancel or pause anytime.' },
              ].map((item,i)=> (
                <motion.div 
                  key={item.q} 
                  initial={{ opacity:0, y:24 }} 
                  whileInView={{ opacity:1, y:0 }} 
                  viewport={{ once:true, amount:.4 }} 
                  transition={{ duration:.55, delay:i*0.04 }} 
                  className="rounded-2xl bg-white border border-gray-100 shadow-md hover:shadow-lg transition-all p-6"
                >
                  <h3 className="font-semibold text-gray-900 tracking-tight mb-2 text-sm">{item.q}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <StandardCTA />
      </main>
      <Footer />
    </div>
  );
}
